import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import {
  getEcosystemEventCtaLabel,
  type UpcomingXrpLedgerEvent,
} from "../data/upcomingXrpLedgerEvents";
import { trackEvent } from "../utils/analytics";
import "./EcosystemEventModal.css";

type EcosystemEventModalProps = {
  event: UpcomingXrpLedgerEvent | null;
  onClose: () => void;
};

const EcosystemEventModal: React.FC<EcosystemEventModalProps> = ({ event, onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!event) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };

    document.addEventListener("keydown", handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [event]);

  if (!event) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const ctaLabel = getEcosystemEventCtaLabel(event);

  return createPortal(
    <div className="ecosystem-event-modal-overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className="ecosystem-event-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={event.description ? descriptionId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ecosystem-event-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="ecosystem-event-modal-media">
          <img src={event.image} alt={event.imageAlt} className="ecosystem-event-modal-img" />
          {event.kinds.length > 0 ? (
            <ul className="ecosystem-event-modal-tags" aria-label="Event type">
              {event.kinds.map((kind) => (
                <li key={kind} className="ecosystem-event-modal-tag">
                  {kind}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="ecosystem-event-modal-copy">
          <p className="ecosystem-event-modal-kicker">
            <time dateTime={event.dateTime}>{event.dateLabel}</time>
            <span aria-hidden="true"> · </span>
            <span>{event.location}</span>
          </p>
          <h2 id={titleId} className="ecosystem-event-modal-title">
            {event.name}
          </h2>
          {event.description ? (
            <p id={descriptionId} className="ecosystem-event-modal-body">
              {event.description}
            </p>
          ) : null}

          {event.href ? (
            <a
              className="ecosystem-event-modal-cta"
              href={event.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("click", "ecosystem-event-modal", event.id)}
            >
              <span>{ctaLabel}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M7 17L17 7M9 7h8v8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EcosystemEventModal;
